import React from "react";
import Icon from "./AppIcon";

/**
 * 🔧 Component-level Error Boundary (2025-12-29)
 * Wraps individual components to isolate errors and show which component failed.
 * Unlike the page-level ErrorBoundary, this shows a small error card.
 */
class ComponentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error(`🚨 Error in component "${this.props.name}":`, error);
    console.error("🚨 Component stack:", errorInfo?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      const { name, minHeight = "200px" } = this.props;
      const errorMessage = this.state.error?.message || "Unknown error";

      return (
        <div
          className="bg-card border border-error/30 rounded-lg p-6 flex flex-col items-center justify-center"
          style={{ minHeight }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon name="AlertCircle" size={20} className="text-error" />
            <span className="font-medium text-error">Component Error</span>
          </div>
          <p className="text-sm text-muted-foreground mb-1">
            Failed to load: <span className="font-medium text-foreground">{name}</span>
          </p>
          <p className="text-xs text-error/80 mb-3 max-w-md text-center">
            {errorMessage}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
            className="text-xs bg-muted hover:bg-muted/80 text-foreground px-3 py-1.5 rounded flex items-center gap-1 transition-colors"
          >
            <Icon name="RefreshCw" size={12} />
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ComponentErrorBoundary;
